/**
 * Note: The color palette naming standard in Redisgn Figma was transferred to the palette file as a multiple of 100.
 * If there is a key name that is not a multiple of 100, it means that this key and value came from outside the Figma.
 * For example: Green/01 = green100, Red/02 = red200.
 */
const colorPalette = {
  whiteAlpha100: 'rgba(255, 255, 255, 0.1)',
  whiteAlpha200: 'rgba(255, 255, 255, 0.2)',
  whiteAlpha300: 'rgba(255, 255, 255, 0.3)',
  whiteAlpha600: 'rgba(255, 255, 255, 0.6)',

  green100: '#EDFCF5',
  green200: '#D4F7E4',
  green300: '#ADEDCD',
  green400: '#78DDB3',
  greenInfo: '#E3FCEF',
  green500: '#5DCEA2',
  green600: '#1EAB79',
  green700: '#118A61',
  green800: '#0E6E51',
  green900: '#0D5841',
  green1000: '#0C4837',
  green1100: '#06281F',
  greenInfoDark: '#00875A',

  red100: '#FFF1F1',
  red200: '#FFE1E1',
  red300: '#FFC7C7',
  red400: '#FFA1A1',
  redInfo: '#FFEBE6',
  red500: '#FF6A6A',
  red600: '#F84343',
  red700: '#E51D1D',
  red800: '#C11414',
  red900: '#9F1515',
  redInfoDark: '#FF5630',
  red1000: '#841818',
  red1100: '#480707',

  grey100: '#F5F7F8',
  grey200: '#EDEFF2',
  grey300: '#DDE1E8',
  grey400: '#C9CED8',
  grey500: '#B2B8C7',
  grey600: '#999FB4',
  grey700: '#878BA4',
  grey800: '#74778E',
  grey900: '#5F6274',
  grey1000: '#50535F',
  grey1100: '#2F3137',

  blue100: '#E4F5FF',
  blue200: '#CFECFF',
  blue300: '#A8DAFF',
  blue400: '#74BFFF',
  blue500: '#3E91FF',
  blue600: '#1363FF',
  blue700: '#0050FF',
  blue800: '#0050FF',
  blue900: '#0047E4',
  blue1000: '#0031B0',
  blue1100: '#001144',

  yellow100: '#FEF8EC',
  yellow200: '#FCEDC9',
  yellow300: '#F8D88F',
  yellow400: '#F4BC4D',
  yellow500: '#F2A72D',
  yellow600: '#EB8515',
  yellow700: '#D0620F',
  yellow800: '#AD4410',
  yellow900: '#8C3514',
  yellow1000: '#732C14',
  yellow1100: '#421506',
};

const palette = {
  bannerBgColor: '#353535',
  dark222: '#222',
  darkGrey: '#292a2c',
  darkGrey80: 'rgb(41,42,44, 0.8)',
  brownishGrey: '#636363',
  lightBlueGrey: '#cfd4de',
  coral: '#fa5851',
  LightCoral: 'rgba(250, 88, 81, 0.6)',
  veryLightCoral: 'rgba(250, 88, 81, 0.1)',
  clearBlue: '#1b75e2',
  blue: '#2c83ed;',
  darkBlue: '#084594',
  lightClearBlue: '#e8f1fc',
  bluishPurple: '#8036e5',
  poisonGreen: '#3cf11a',
  fadedOrange: '#fe9739',
  veryLightFadedOrange: 'rgba(254, 151, 57, 0.1)',
  violet: '#901be2',
  white: '#ffffff',
  whiteSmoke: '#f5f5f5',
  tomato: '#e2231b',
  error: '#d32f2f',
  darkGray: '#525252',
  gray87: '#dedede',
  lynxWhite: '#f7f7f7',
  lightSuccess: '#e0f7dc',
  darkSuccess: '#2fbd14',
  darkGreen: '#04a985',
  lightGreenScore: '#4BA687',
  purple: '#682a7a',
  pinkSwan: '#b6b5b5',
  black: 'rgb(0,0,0)',
  blackAlpha: 'rgba(0,0,0,0.85)',
  lightBlack: '#434343',
  veryLightBlack: 'rgba(58,58,58, 0.1)',
  grey: '#757575',
  lightGray: '#f3f3f3',
  inputBorderColor: colorPalette.grey200,
  inputBorderColorActive: '#000000',
  inputPlaceholderColor: colorPalette.grey600,
  matterHorn: '#4b4b4b',
  mountainMist: '#94979c',
  gray34: '#575757',
  nero: '#282828',
  eclipse: '#3a3a3a',
  lightEclipse: 'rgba(58, 58, 58, 0.2)',
  black11: '#0A0A0A',
  darkPurple: '#7b1fa1',
  veryLightDarkPurple: 'rgba(123, 31, 161, 0.1)',
  veryLightGreenOpacity: 'rgba(4, 169, 133, 0.1)',
  lightGreyButtonBackground: 'rgba(207, 212, 222, .2)',
  validGreen: '#00c269',
  gray: '#eeeeee',
  green: '#5cc85c',
  lightGreen: '#E8F5F2',
  lightYellow: '#faf8f0',
  athensGray: '#f9fafb',
  lightsSilver: '#d8d8d8',
  hesInfoBoxBackground: '#fef4eb',
  greenSuccess: '#5acc45',
  graniteGray: '#5f6061',
  coral_01: '#fdeeec',
  lightGreenBorder: ' #eef2f6',
  iron: '#CFD4DE',
  red: '#E2231B',
  yellow: '#FFB800',
  persianRed: '#D1392B',
  cornsilk: '#fcf8e3',
  wheat: '#faebcc',
  peru: '#8a6d3b',
  primaryGrey: '#221e1f',
  darkGreenScore: '#4CA671',
  burntSienna: '#E96358',
  primaryBrand: colorPalette.grey1100,
  primaryBrandSoft: '#f5f7fd',
  primaryBrandDark: colorPalette.grey400,
  inputColor: '#636363',
  inputBackgroundColor: '#f7f7f7',
  errorColor: '#e2231b',
  blueGrey: '#4C5C7C',
  backgroundColor: '#F9FBFC',
  ...colorPalette,
  orange50: '#FFFAE6',
  orange500: '#FF8B00',
  orange: '#FE9739',
  grey40: '#666666',
  deepTeal: '#00697C',
  // Figma Teal/T400 — weekend holiday accent (MAP-takvim-guncelleme). Distinct
  // from `deepTeal` (#00697C); used for the weekend cell dot and chip accent.
  teal: '#00a3bf',
  black12: '#0D0D0D',
  NL100: '#7A869A',
  NL20: '#F4F5F7',
  NL30: '#EBECF0',
  // Figma Neutral/NL40 — holiday tab bar inactive-pill border (MAP-takvim-guncelleme
  // CORR-5). Close to `grey300` (#DDE1E8) but not identical; dedicated token for
  // pixel-perfect match.
  NL40: '#DFE1E6',
  NL80: '#97A0AF',
  D30: '#D6D6D6',
  B50: '#DEEBFF',
  B300: '#0047FF',
  D400: '#292A2C',
};

export type Palette = typeof palette;

export default palette;
